import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/**
 * Keyboard behavior for a list you choose one thing from.
 *
 * ## Why this exists at all
 *
 * We do not take a component library, so nobody supplies this for us. That is a
 * deliberate trade — more work over more workaround — and the bill comes due
 * here: arrow keys, Home/End, Escape, typeahead and the active-descendant
 * bookkeeping are what a native `<select>` gave away for free, and what a custom
 * one has to earn back. A dropdown that cannot be driven from the keyboard is
 * not a smaller version of a working one; it is broken for anybody who does not
 * use a mouse, and it fails silently for everybody else.
 *
 * It is a hook rather than a component because the behavior outlives any one
 * shape. A select, a combobox and a menu are three different things on screen
 * and the same thing under the fingers.
 *
 * ## Typeahead
 *
 * Typing "sh" jumps to the first option starting with it — the single most-used
 * feature of a native select, and the one most often left out of a hand-built
 * one. Keystrokes accumulate while they keep arriving and the
 * buffer clears after a pause, so "sh" finds shkia while a slow "s… h" is two
 * separate jumps. Matching is prefix-first because that is what a native select
 * does and what fingers expect; a substring match would send "a" somewhere
 * surprising in a list where every label contains one.
 *
 * ## Two handlers, because a search field is the typeahead
 *
 * `onKeyDown` is for a list somebody types AT. `onSearchKeyDown` is for one
 * whose panel holds a field they type INTO, and it gives three keys back to the
 * field: a printable character, a space and Home/End.
 *
 * They cannot share one handler, because on a field the list's reading of those
 * keys is not merely redundant — it destroys the typing. Typeahead swallows a
 * character whenever it matches an option, so letters vanish from the field in
 * the pattern of whatever happens to match, which reads as a broken input
 * rather than as a list doing something. Space picks the active option instead
 * of separating two words, and Home/End jump the list instead of moving the
 * caret. Arrows, Enter, Escape and Tab still belong to the list: those are how
 * somebody reaches and takes an option without leaving the field.
 *
 * ## A typed search makes its first match the active option
 *
 * As the search narrows, the first enabled match is the one Enter takes, so
 * typing a name and pressing Enter picks it — the convention of Base UI's
 * combobox, and what a person typing into a list expects. The arrows move on
 * from there. A search matching nothing leaves nothing active, so Enter does
 * nothing. Clearing the search returns to where opening lands: the current
 * value.
 */
const TYPEAHEAD_RESET_MS = 500

export type ListboxItem = { value: string; label: string; disabled?: boolean }

/** The first option that can be taken; -1 where none can. */
export const firstEnabledIndex = (items: readonly ListboxItem[]): number =>
  items.findIndex((o) => !o.disabled)

/**
 * The active option for a list as a search has left it: the first enabled
 * match while something is typed, and the current value — or the first
 * option — once the search is empty again. -1 where nothing can be taken.
 */
export function activeForSearch(
  items: readonly ListboxItem[],
  search: string,
  value: string | null,
): number {
  if (search.trim()) return firstEnabledIndex(items)
  const at = items.findIndex((o) => o.value === value)
  return at >= 0 && !items[at]?.disabled ? at : firstEnabledIndex(items)
}

/** Everything the key routing reads, so that it reads nothing else. */
type KeyContext = {
  /** Whether the panel holds a field the keystroke is meant for. */
  typingInAField: boolean
  open: boolean
  items: ListboxItem[]
  activeIndex: number
  setActiveIndex: (next: number | ((i: number) => number)) => void
  openAt: () => void
  close: () => void
  onPick: (value: string) => void
  closeOnPick: boolean
  step: (i: number, by: number) => number
  firstEnabled: number
  lastEnabled: number
  /** Jump to the option this key spells, saying whether one was found. */
  typeahead: (key: string) => boolean
}

/**
 * Which keys the list answers, and which it leaves for the field beneath it.
 *
 * Pure and exported, because it is a decision rather than a behavior: what it
 * does with a keystroke follows from its arguments alone. That is also what
 * makes the case worth testing reachable — whether a character survives to
 * reach a search field is invisible in a rendered dropdown, and shows up only
 * as letters going missing while somebody types.
 */
export function listboxKey(e: KeyboardEvent, d: KeyContext): void {
  // A closed control opens on the keys that mean "show me the choices", and
  // Enter is not one of them — inside a form Enter belongs to the form.
  if (!d.open) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === ' ') {
      e.preventDefault()
      d.openAt()
    }
    return
  }

  /** Take the active option, where there is one to take. */
  const pick = () => {
    const picked = d.items[d.activeIndex]
    if (picked && !picked.disabled) {
      e.preventDefault()
      d.onPick(picked.value)
      if (d.closeOnPick) d.close()
    }
  }

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      d.setActiveIndex((i) => d.step(i, 1))
      break
    case 'ArrowUp':
      e.preventDefault()
      d.setActiveIndex((i) => d.step(i, -1))
      break
    case 'Home':
      // In a field these are the caret's, and a list that takes them stops the
      // field being a field.
      if (d.typingInAField) break
      e.preventDefault()
      d.setActiveIndex(d.firstEnabled)
      break
    case 'End':
      if (d.typingInAField) break
      e.preventDefault()
      d.setActiveIndex(d.lastEnabled)
      break
    case 'Escape':
      e.preventDefault()
      d.close()
      break
    case 'Enter':
      pick()
      break
    case ' ':
      // A space between two words of a name, not a choice.
      if (d.typingInAField) break
      pick()
      break
    case 'Tab':
      // Tab commits nothing and leaves — the same as a native select, and
      // the reason Escape is not the only way out.
      d.close()
      break
    default:
      // The field IS the typeahead. Running both means the character is taken
      // whenever it matches an option, so the letters that survive are the ones
      // that happened not to — which reads as an input dropping what is typed.
      if (d.typingInAField) break
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (d.typeahead(e.key)) e.preventDefault()
      }
  }
}

export function useListbox({
  items,
  value,
  onPick,
  closeOnPick = true,
  open,
  onOpenChange,
  search = '',
}: {
  items: ListboxItem[]
  value: string | null
  onPick: (value: string) => void
  /**
   * Whether picking ends the interaction. False where the control builds a SET:
   * choosing five things would otherwise be five round trips through the
   * trigger, and the panel would shut on the first tick.
   */
  closeOnPick?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * The search `items` is already filtered by, as the filter reads it — so a
   * keystroke the filter ignores, such as punctuation, leaves the cursor where
   * it is. A change of it moves the active option (`activeForSearch`).
   */
  search?: string
}) {
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const typed = useRef({ buffer: '', at: 0 })

  /* Keyed on the search alone: the items and the value are read as they stand
     when it changes, and a new items array for an unchanged search — a caller
     rebuilding its options — must not move the cursor. */
  const latest = useRef({ items, value })
  latest.current = { items, value }
  const searched = useRef(search)
  useEffect(() => {
    if (searched.current === search) return
    searched.current = search
    setActiveIndex(
      activeForSearch(latest.current.items, search, latest.current.value),
    )
  }, [search])

  /** Skip disabled items when moving, so a run of them cannot trap the cursor. */
  const step = useCallback(
    (from: number, delta: number) => {
      if (items.length === 0) return -1
      let i = from
      for (let n = 0; n < items.length; n++) {
        i = (i + delta + items.length) % items.length
        if (!items[i]?.disabled) return i
      }
      return -1
    },
    [items],
  )

  const firstEnabled = useMemo(() => firstEnabledIndex(items), [items])
  const lastEnabled = useMemo(() => {
    for (let i = items.length - 1; i >= 0; i--)
      if (!items[i]?.disabled) return i
    return -1
  }, [items])

  /**
   * Opening lands on the current value rather than the top of the list: the
   * thing you already chose is the thing you are most likely adjusting.
   */
  const openAt = useCallback(() => {
    const at = items.findIndex((o) => o.value === value)
    setActiveIndex(at >= 0 && !items[at]?.disabled ? at : firstEnabled)
    onOpenChange(true)
  }, [items, value, firstEnabled, onOpenChange])

  const close = useCallback(() => {
    setActiveIndex(-1)
    typed.current = { buffer: '', at: 0 }
    onOpenChange(false)
  }, [onOpenChange])

  const typeahead = useCallback(
    (key: string) => {
      const now = performance.now()
      const buffer =
        now - typed.current.at > TYPEAHEAD_RESET_MS
          ? key.toLowerCase()
          : typed.current.buffer + key.toLowerCase()
      typed.current = { buffer, at: now }
      const at = items.findIndex(
        (o) => !o.disabled && o.label.toLowerCase().startsWith(buffer),
      )
      if (at >= 0) setActiveIndex(at)
      return at >= 0
    },
    [items],
  )

  const handle = useCallback(
    (e: KeyboardEvent, typingInAField: boolean) =>
      listboxKey(e, {
        typingInAField,
        open,
        items,
        activeIndex,
        setActiveIndex,
        openAt,
        close,
        onPick,
        closeOnPick,
        step,
        firstEnabled,
        lastEnabled,
        typeahead,
      }),
    [
      open,
      items,
      activeIndex,
      openAt,
      close,
      onPick,
      closeOnPick,
      step,
      firstEnabled,
      lastEnabled,
      typeahead,
    ],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => handle(e, false),
    [handle],
  )
  const onSearchKeyDown = useCallback(
    (e: KeyboardEvent) => handle(e, true),
    [handle],
  )

  return {
    activeIndex,
    setActiveIndex,
    openAt,
    close,
    onKeyDown,
    onSearchKeyDown,
  }
}
